import { useGetRegistryCheckoutSettingsQuery } from './LoadingRegistryCheckoutSettings.graphql.generated';
import type { StripeElementsProviderLazyConfig } from '@shared/core/stripe';
import { useStripeElementsContext } from '@shared/core/stripe';
import React, { useEffect, useMemo, useState } from 'react';

type LoadingRegistryCheckoutSettingsProps = Readonly<{
  eventId: string | undefined;
  /** Content to display upon success */
  children: React.ReactNode;
  /** Content to display upon unrecoverable failure */
  fallbackContent?: React.ReactNode;
}>;

export const LoadingRegistryCheckoutSettings: React.FC<LoadingRegistryCheckoutSettingsProps> = ({ children, eventId, fallbackContent }) => {
  const { data, error, loading } = useGetRegistryCheckoutSettingsQuery({
    variables: {
      eventId: eventId ?? '',
    },
    skip: !eventId,
    batchMode: 'fast',
  });
  const checkoutSettings = data?.getRegistryCheckoutSettings;

  const lazyConfig = useMemo<StripeElementsProviderLazyConfig | null>(() => (!(error || loading) ? (checkoutSettings ?? null) : null), [checkoutSettings, error, loading]);

  // an intentional decoupling of the data load & render cycles:
  //   before this change, Stripe <iframe /> loading would complete before setLazyConfig got called.
  //   The Sad Path:
  //   1. mount — StripeElements.Provider has no config; stripe={null}
  //   2. data arrives — lazyConfig becomes truthy; children (incl. <ExpressCheckoutElement>) render immediately
  //   3. browser paints — Stripe iframe is in the DOM; Stripe fires onReady
  //   4. useEffect runs — setLazyConfig finally called; StripeElements.Provider receives the key
  //   The Happy Path:
  //   with configApplied, step 2 renders null instead; step 4 sets configApplied=true; children
  //   mount on the subsequent render, after the provider already holds the config.
  const [configApplied, setConfigApplied] = useState(false);

  const { setLazyConfig } = useStripeElementsContext();
  useEffect(() => {
    if (lazyConfig) {
      setLazyConfig(lazyConfig);
      setConfigApplied(true);
    }
  }, [lazyConfig, setLazyConfig, setConfigApplied]);

  if (error && fallbackContent) {
    // we have reached a final error state
    return <>{fallbackContent}</>;
  }
  if (configApplied) {
    // we have received our critical configuration
    return <>{children}</>;
  }
  // until we are configured, we will not render anything
  return null;
};
